import { useCustomersContext } from '@src/hooks/useCustomer';
import {
  AddressStatus,
  Button,
  CustomerAddressStatusEnum,
  FormControlSizes,
  HStack,
  logger,
  NotificationVariants,
  useGlobalToasts,
  type CustomDrawerContentRenderProps,
  type RequiredProperties,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import type { CustomerAddressesDrawerEntity } from './index';

export function ConfirmAddressStatusField({
  form,
  isEditing,
  onFormUpdate,
  originalEntity,
}: RequiredProperties<CustomDrawerContentRenderProps<CustomerAddressesDrawerEntity>, 'originalEntity'>) {
  const { confirmCustomerAddress } = useCustomersContext();
  const { add: addToast } = useGlobalToasts();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleOnConfirm = useCallback(() => {
    setIsConfirming(true);
    confirmCustomerAddress(originalEntity.CustomerAddressID)
      .then(() => {
        onFormUpdate('Status', CustomerAddressStatusEnum.Confirmed);
      })
      .catch(e => {
        const errorDescription = 'Could not confirm customer address.';
        addToast({ text: e?.toString() || errorDescription, variant: NotificationVariants.Negative });
        logger.error(new Error(errorDescription));
      })
      .finally(() => setIsConfirming(false));
  }, [confirmCustomerAddress, originalEntity.CustomerAddressID, onFormUpdate, addToast]);

  return (
    <HStack gap="spacing8" pb="spacing4" justifyContent="space-between">
      <AddressStatus status={form.Status} />
      {isEditing && form.Status === CustomerAddressStatusEnum.Pending && (
        <Button
          size={FormControlSizes.Small}
          onClick={handleOnConfirm}
          loading={isConfirming}
          data-testid="confirm-customer-address-button"
        >
          Confirm
        </Button>
      )}
    </HStack>
  );
}
